import React, { useState, useEffect } from 'react';
import {
  Save,
  Shield,
  Mail,
  Globe,
  Bell,
  Key,
  CheckCircle,
  AlertCircle,
  Loader,
  Send,
  CreditCard,
  Bot,
} from 'lucide-react';
import adminSettingsService from '../services/adminSettingsService';

const SystemSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [notification, setNotification] = useState(null); // { type: 'success'|'error', message }
  const [activatingProvider, setActivatingProvider] = useState(null); // provider key being set active

  useEffect(() => {
    adminSettingsService.getSettings()
      .then((data) => setSettings(data || {}))
      .catch((err) => console.error('Failed to load settings:', err))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminSettingsService.saveSettings(settings);
      showNotification('success', 'Settings saved successfully!');
    } catch (err) {
      showNotification('error', err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    const toEmail = settings['smtp.from_email'] || settings['smtp.username'] || '';
    if (!settings['smtp.host'] || !toEmail) {
      showNotification('error', 'Please fill in SMTP host and username/from email before testing');
      return;
    }
    try {
      setTestingEmail(true);
      const result = await adminSettingsService.sendTestEmail({
        host: settings['smtp.host'] || '',
        port: settings['smtp.port'] || '587',
        username: settings['smtp.username'] || '',
        password: settings['smtp.password'] || '',
        encryption: settings['smtp.encryption'] || 'tls',
        toEmail,
      });
      if (result?.success) {
        showNotification('success', result.message || 'Test email sent!');
      } else {
        showNotification('error', result?.message || 'Test email failed');
      }
    } catch (err) {
      showNotification('error', err.message || 'Test email failed');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSetActive = async (providerKey) => {
    try {
      setActivatingProvider(providerKey);
      await adminSettingsService.saveSettings({ 'llm.active_provider': providerKey });
      set('llm.active_provider', providerKey);
      const labels = { anthropic: 'Anthropic (Claude)', openai: 'OpenAI', mixtral: 'Mixtral', ollama: 'Ollama (Local)' };
      showNotification('success', `${labels[providerKey]} is now the active LLM provider.`);
    } catch (err) {
      showNotification('error', err.message || 'Failed to switch provider');
    } finally {
      setActivatingProvider(null);
    }
  };

  const handleToggleEnabled = (providerKey, newEnabled) => {
    if (!newEnabled && settings['llm.active_provider'] === providerKey) {
      const labels = { anthropic: 'Anthropic (Claude)', openai: 'OpenAI', mixtral: 'Mixtral', ollama: 'Ollama (Local)' };
      showNotification('error', `Switch to another provider before disabling ${labels[providerKey]}.`);
      return;
    }
    set(`llm.${providerKey}_enabled`, newEnabled ? 'true' : 'false');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600 mt-1">Configure platform-wide settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`rounded-lg p-4 flex items-center gap-3 ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      {/* General Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">General Settings</h2>
            <p className="text-sm text-gray-600">Basic platform configuration</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name</label>
            <input
              type="text"
              value={settings['general.platform_name'] || ''}
              onChange={(e) => set('general.platform_name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
            <input
              type="email"
              value={settings['general.support_email'] || ''}
              onChange={(e) => set('general.support_email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Website</label>
            <input
              type="url"
              value={settings['general.website'] || ''}
              onChange={(e) => set('general.website', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Timezone</label>
            <select
              value={settings['general.timezone'] || 'America/New_York'}
              onChange={(e) => set('general.timezone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Dubai">Dubai (GST)</option>
              <option value="Asia/Kolkata">India (IST)</option>
              <option value="Asia/Singapore">Singapore (SGT)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Email Configuration</h2>
            <p className="text-sm text-gray-600">SMTP settings for outgoing email</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
            <input
              type="text"
              value={settings['smtp.host'] || ''}
              onChange={(e) => set('smtp.host', e.target.value)}
              placeholder="smtp.sendgrid.net"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
              <input
                type="number"
                value={settings['smtp.port'] || '587'}
                onChange={(e) => set('smtp.port', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Encryption</label>
              <select
                value={settings['smtp.encryption'] || 'tls'}
                onChange={(e) => set('smtp.encryption', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username / From Email</label>
            <input
              type="text"
              value={settings['smtp.username'] || ''}
              onChange={(e) => set('smtp.username', e.target.value)}
              placeholder="noreply@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
            <input
              type="password"
              value={settings['smtp.password'] || ''}
              onChange={(e) => set('smtp.password', e.target.value)}
              placeholder="Leave blank to keep existing"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Leave blank to keep the existing saved password unchanged.</p>
          </div>

          <button
            type="button"
            onClick={handleTestEmail}
            disabled={testingEmail}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {testingEmail ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {testingEmail ? 'Sending…' : 'Test Email Configuration'}
          </button>
        </div>
      </div>

      {/* Billing Email Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Billing Email Identity</h2>
            <p className="text-sm text-gray-600">
              Sender name and address used for invoice and receipt emails sent to customers.
              Leave blank to fall back to SMTP username and platform name.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Sender Name</label>
            <input
              type="text"
              value={settings['billing.email.from_name'] || ''}
              onChange={(e) => set('billing.email.from_name', e.target.value)}
              placeholder={settings['general.platform_name'] || 'SaaS Platform'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Displayed as the "From" name in billing emails (e.g. "Acme Billing Team").
              Defaults to the platform name if blank.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing From Email Address</label>
            <input
              type="email"
              value={settings['billing.email.from_email'] || ''}
              onChange={(e) => set('billing.email.from_email', e.target.value)}
              placeholder={settings['smtp.username'] || 'billing@yourcompany.com'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              The "From" email address for invoices and receipts (e.g. billing@yourcompany.com).
              Defaults to the SMTP username if blank.
            </p>
          </div>

          {/* Renewal configuration */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Renewal Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CMMS Grace Period (days)
                </label>
                <input
                  type="number"
                  min="0"
                  value={settings['billing.cmms.grace_period_days'] || '3'}
                  onChange={(e) => set('billing.cmms.grace_period_days', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="3"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Days to allow CMMS access after subscription expires before full suspension.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renewal Reminder (days before expiry)
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings['billing.renewal_reminder_days'] || '7'}
                  onChange={(e) => set('billing.renewal_reminder_days', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="7"
                />
                <p className="mt-1 text-xs text-gray-500">
                  How many days before expiry to send the renewal reminder email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Provider Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">AI Provider Configuration</h2>
            <p className="text-sm text-gray-600">
              Configure LLM providers. Enable a provider to make it available, then set it as active.
              Changes take effect within 5 minutes without restarting any service.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Anthropic */}
          {(() => {
            const providerKey = 'anthropic';
            const isEnabled = settings['llm.anthropic_enabled'] === 'true';
            const isActive = settings['llm.active_provider'] === providerKey;
            return (
              <div className={`border-2 rounded-xl p-5 transition-colors ${isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟣</span>
                    <span className="font-semibold text-gray-900">Anthropic (Claude)</span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active ✓</span>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => handleToggleEnabled(providerKey, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    <span className="ml-2 text-sm text-gray-600">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
                <div className={`space-y-3 ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                    <input type="password" value={settings['api.anthropic_key'] || ''} onChange={(e) => set('api.anthropic_key', e.target.value)} placeholder="sk-ant-…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={settings['llm.anthropic_model'] || ''} onChange={(e) => set('llm.anthropic_model', e.target.value)} placeholder="claude-sonnet-4-6" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                      <input type="number" min="100" max="32000" value={settings['llm.anthropic_max_tokens'] || '1024'} onChange={(e) => set('llm.anthropic_max_tokens', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                      <input type="number" step="0.1" min="0" max="2" value={settings['llm.anthropic_temperature'] || '0.7'} onChange={(e) => set('llm.anthropic_temperature', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                  </div>
                </div>
                {!isActive && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleSetActive(providerKey)}
                      disabled={!isEnabled || activatingProvider === providerKey}
                      className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {activatingProvider === providerKey && <Loader className="w-3 h-3 animate-spin" />}
                      Set as Active
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* OpenAI */}
          {(() => {
            const providerKey = 'openai';
            const isEnabled = settings['llm.openai_enabled'] === 'true';
            const isActive = settings['llm.active_provider'] === providerKey;
            return (
              <div className={`border-2 rounded-xl p-5 transition-colors ${isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🟢</span>
                    <span className="font-semibold text-gray-900">OpenAI</span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active ✓</span>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => handleToggleEnabled(providerKey, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    <span className="ml-2 text-sm text-gray-600">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
                <div className={`space-y-3 ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                    <input type="password" value={settings['api.openai_key'] || ''} onChange={(e) => set('api.openai_key', e.target.value)} placeholder="sk-proj-…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={settings['llm.openai_model'] || ''} onChange={(e) => set('llm.openai_model', e.target.value)} placeholder="gpt-4o" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                      <input type="number" min="100" max="32000" value={settings['llm.openai_max_tokens'] || '1024'} onChange={(e) => set('llm.openai_max_tokens', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                      <input type="number" step="0.1" min="0" max="2" value={settings['llm.openai_temperature'] || '0.7'} onChange={(e) => set('llm.openai_temperature', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                  </div>
                </div>
                {!isActive && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleSetActive(providerKey)}
                      disabled={!isEnabled || activatingProvider === providerKey}
                      className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {activatingProvider === providerKey && <Loader className="w-3 h-3 animate-spin" />}
                      Set as Active
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Mixtral (Self-hosted) */}
          {(() => {
            const providerKey = 'mixtral';
            const isEnabled = settings['llm.mixtral_enabled'] === 'true';
            const isActive = settings['llm.active_provider'] === providerKey;
            return (
              <div className={`border-2 rounded-xl p-5 transition-colors ${isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔵</span>
                    <span className="font-semibold text-gray-900">Mixtral <span className="text-sm font-normal text-gray-500">(Self-hosted)</span></span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active ✓</span>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => handleToggleEnabled(providerKey, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                    <span className="ml-2 text-sm text-gray-600">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
                <div className={`space-y-3 ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base URL <span className="text-red-500">*</span></label>
                    <input type="text" value={settings['llm.mixtral_base_url'] || ''} onChange={(e) => set('llm.mixtral_base_url', e.target.value)} placeholder="http://192.168.1.100:8000/v1" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">Base URL of your OpenAI-compatible GPU server (e.g. Ollama, vLLM, llama.cpp).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key <span className="text-gray-400">(optional)</span></label>
                    <input type="password" value={settings['api.mixtral_key'] || ''} onChange={(e) => set('api.mixtral_key', e.target.value)} placeholder="Leave blank for no-auth local server" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={settings['llm.mixtral_model'] || ''} onChange={(e) => set('llm.mixtral_model', e.target.value)} placeholder="mistralai/Mixtral-8x7B-Instruct-v0.1" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                      <input type="number" min="100" max="32000" value={settings['llm.mixtral_max_tokens'] || '1024'} onChange={(e) => set('llm.mixtral_max_tokens', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                      <input type="number" step="0.1" min="0" max="2" value={settings['llm.mixtral_temperature'] || '0.7'} onChange={(e) => set('llm.mixtral_temperature', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                  </div>
                </div>
                {!isActive && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleSetActive(providerKey)}
                      disabled={!isEnabled || activatingProvider === providerKey}
                      className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {activatingProvider === providerKey && <Loader className="w-3 h-3 animate-spin" />}
                      Set as Active
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Ollama (Local Self-hosted) */}
          {(() => {
            const providerKey = 'ollama';
            const isEnabled = settings['llm.ollama_enabled'] === 'true';
            const isActive = settings['llm.active_provider'] === providerKey;
            return (
              <div className={`border-2 rounded-xl p-5 transition-colors ${isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🦙</span>
                    <span className="font-semibold text-gray-900">Ollama <span className="text-sm font-normal text-gray-500">(Local Self-hosted)</span></span>
                    {isActive && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active ✓</span>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={isEnabled} onChange={(e) => handleToggleEnabled(providerKey, e.target.checked)} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                  </label>
                </div>

                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <strong>Setup:</strong> Install <a href="https://ollama.com" target="_blank" rel="noreferrer" className="underline">Ollama</a> on your server, then run{' '}
                  <code className="bg-amber-100 px-1 rounded font-mono">ollama pull llama3.2:3b</code> (or another model).
                  Ollama exposes an OpenAI-compatible API at <code className="bg-amber-100 px-1 rounded font-mono">http://localhost:11434/v1</code>.
                </div>

                <div className={`space-y-3 ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
                    <input type="text" value={settings['llm.ollama_base_url'] || ''} onChange={(e) => set('llm.ollama_base_url', e.target.value)} placeholder="http://localhost:11434/v1" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">Default: <code>http://localhost:11434/v1</code> — change only if Ollama runs on a different host/port.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key <span className="text-gray-400">(optional)</span></label>
                    <input type="password" value={settings['api.ollama_key'] || ''} onChange={(e) => set('api.ollama_key', e.target.value)} placeholder="Leave blank for no-auth local server" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">Required if your Ollama instance is behind an API gateway or reverse proxy that enforces authentication.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={settings['llm.ollama_model'] || ''} onChange={(e) => set('llm.ollama_model', e.target.value)} placeholder="llama3.2:3b" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    <p className="text-xs text-gray-500 mt-1">
                      Popular models: <code>llama3.2:3b</code> · <code>llama3.1:8b</code> · <code>mistral:7b</code> · <code>qwen2.5:7b</code> · <code>phi3:mini</code>
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                      <input type="number" min="100" max="32000" value={settings['llm.ollama_max_tokens'] || '1024'} onChange={(e) => set('llm.ollama_max_tokens', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                      <input type="number" step="0.1" min="0" max="2" value={settings['llm.ollama_temperature'] || '0.7'} onChange={(e) => set('llm.ollama_temperature', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                    </div>
                  </div>
                </div>
                {!isActive && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleSetActive(providerKey)}
                      disabled={!isEnabled || activatingProvider === providerKey}
                      className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {activatingProvider === providerKey && <Loader className="w-3 h-3 animate-spin" />}
                      Set as Active
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Security &amp; Authentication</h2>
            <p className="text-sm text-gray-600">Platform security settings</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'security.require_email_verification', label: 'Require Email Verification', desc: 'New users must verify their email', defaultVal: true },
            { key: 'security.require_2fa', label: 'Two-Factor Authentication', desc: 'Require 2FA for admin accounts', defaultVal: true },
            { key: 'security.password_expiry', label: 'Password Expiry', desc: 'Force password change every 90 days', defaultVal: false },
          ].map(({ key, label, desc, defaultVal }) => {
            const checked = settings[key] !== undefined ? settings[key] === 'true' : defaultVal;
            return (
              <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{label}</div>
                  <div className="text-sm text-gray-600">{desc}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => set(key, e.target.checked ? 'true' : 'false')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            );
          })}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
            <input
              type="number"
              value={settings['security.session_timeout_minutes'] || '60'}
              onChange={(e) => set('security.session_timeout_minutes', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Notification Preferences</h2>
            <p className="text-sm text-gray-600">Admin notification settings</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'notify.new_signups', label: 'New User Signups', desc: 'Email notification for new registrations', defaultVal: true },
            { key: 'notify.support_tickets', label: 'Support Tickets', desc: 'Notify when new ticket is created', defaultVal: true },
            { key: 'notify.system_alerts', label: 'System Alerts', desc: 'Critical system notifications', defaultVal: true },
          ].map(({ key, label, desc, defaultVal }) => {
            const checked = settings[key] !== undefined ? settings[key] === 'true' : defaultVal;
            return (
              <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{label}</div>
                  <div className="text-sm text-gray-600">{desc}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => set(key, e.target.checked ? 'true' : 'false')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* API Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Key className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">API Configuration</h2>
            <p className="text-sm text-gray-600">External service integrations (stored securely)</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { key: 'api.stripe_secret_key', label: 'Stripe Secret Key', placeholder: 'sk_live_…' },
            { key: 'api.sendgrid_key', label: 'SendGrid API Key', placeholder: 'SG.…' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
              <input
                type="password"
                value={settings[key] || ''}
                onChange={(e) => set(key, e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Save */}
      <div className="flex justify-end pb-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;
